import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from 'recharts';
import {
  ChevronLeft, ChevronRight, RotateCw, Power, Trash2, Activity, Cable, Phone,
  AlertTriangle, CheckCircle2, XCircle, Loader2, Copy, Check, Pause, Play, Wifi,
  Sparkles, Edit3, Cpu,
} from 'lucide-react';
import { onts, serviceProfiles, type OntDetailData, type ServiceProfileItem } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

function rxColor(rx: number | null): string {
  if (rx === null) return 'text-text-faint';
  if (rx < -28) return 'text-danger';
  if (rx < -25) return 'text-warning';
  return 'text-success';
}

function formatUptime(sec: number | null): string {
  if (!sec) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}j ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBitrate(kbps: number | null): string {
  if (!kbps) return '—';
  if (kbps >= 1_000_000) return `${(kbps / 1_000_000).toFixed(1)} Gbps`;
  if (kbps >= 1_000) return `${kbps / 1000} Mbps`;
  return `${kbps} kbps`;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    up:           ['badge-up',   'up'],
    down:         ['badge-down', 'down'],
    discovering:  ['badge-info', 'discovering'],
    admin_up:     ['badge-up',   'admin up'],
    admin_down:   ['badge-down', 'admin down'],
    unknown:      ['badge',      'unknown'],
  };
  const [cls, label] = map[status] ?? ['badge', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function Copyable({ value, mono = true }: { value: string | null | undefined; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-text-faint">—</span>;
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className={`inline-flex items-center gap-1 hover:bg-surface-hi/60 -mx-1 px-1 rounded transition-colors group ${mono ? 'font-mono' : ''}`}
      title={copied ? 'Copié' : 'Cliquer pour copier'}
    >
      <span>{value}</span>
      {copied
        ? <Check size={9} className="text-success" />
        : <Copy size={9} className="opacity-0 group-hover:opacity-60 transition-opacity" />}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function OntDetail() {
  const { id } = useParams();
  const ontId = Number(id);
  const toast = useToast();
  const qc = useQueryClient();
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  const { data: ont, isLoading } = useQuery({
    queryKey: ['ont', ontId],
    queryFn: () => onts.get(ontId),
    refetchInterval: 30_000,
  });

  // Live refresh on page open : fire silently, update cache, set timestamp
  const refresh = useMutation({
    mutationFn: () => onts.refresh(ontId),
    onSuccess: (fresh) => {
      qc.setQueryData(['ont', ontId], fresh);
      qc.invalidateQueries({ queryKey: ['ont-optical', ontId] });
      setRefreshedAt(Date.now());
    },
    onError: (e: any) => {
      // Silent fail — keep cached data, show toast only if explicit user action
      console.warn('Live refresh failed:', e?.response?.data?.detail ?? e.message);
    },
  });

  useEffect(() => {
    if (!ontId) return;
    refresh.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ontId]);

  const { data: optical } = useQuery({
    queryKey: ['ont-optical', ontId],
    queryFn: () => onts.opticalHistory(ontId, 7),
    enabled: !!ont,
  });

  const { data: timeline } = useQuery({
    queryKey: ['ont-events', ontId],
    queryFn: () => onts.events(ontId, 14),
    enabled: !!ont,
  });

  if (isLoading || !ont) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted gap-2">
        <Loader2 size={16} className="animate-spin" />
        <span>Chargement de l'ONT…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 h-9 px-4 border-b border-border bg-bg-2 text-xs text-text-muted shrink-0">
        <Link to="/app" className="hover:text-text">Filos NMS</Link>
        <ChevronRight size={11} className="text-text-faint" />
        <span>Telkel</span>
        <ChevronRight size={11} className="text-text-faint" />
        <Link to="/app/onts" className="hover:text-text">ONTs</Link>
        <ChevronRight size={11} className="text-text-faint" />
        <span className="text-text font-medium">{ont.customer?.name ?? `ONT ${ont.vendor_serial}`}</span>
        <div className="ml-auto font-mono text-text-faint text-[10.5px]">
          tenant <span className="text-brand">telkel</span> · OLT {ont.olt.name} · PON {ont.pon.vendor_local_id}/{ont.vendor_local_id}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Sticky strip — directement enfant du scroll container, top-0 colle au breadcrumb */}
        <div className="sticky top-0 z-20 px-3 pt-3 pb-2 bg-bg/95 backdrop-blur-sm border-b border-border">
          <StickyStatusStrip
            ont={ont}
            refreshing={refresh.isPending}
            refreshedAt={refreshedAt}
            onManualRefresh={() => refresh.mutate()}
          />
        </div>

        <div className="p-3 flex flex-col gap-3">
          <HeaderCard ont={ont} onRefresh={() => refresh.mutate()} refreshing={refresh.isPending} />
          <TileRow ont={ont} />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-3">
            <div className="flex flex-col gap-3 min-w-0">
              <SubElements ont={ont} />
              <CpeDevicesCard ont={ont} />
              <SubscriptionCard ont={ont} />
              <OpticalHistoryCard data={optical?.data ?? []} />
              <EventsTimeline events={timeline?.data.events ?? []} alarms={timeline?.data.alarms ?? []} />
              <AdvancedSection ont={ont} />
            </div>
            <div className="flex flex-col gap-3">
              <ActionsRail ont={ont} onAction={(msg) => toast.push({ kind: 'success', title: msg })} />
              <TopologyRail ont={ont} />
              <DiagnosticsRail ont={ont} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sticky status strip — 6 stat cells, the "in 5 sec, is it OK?" answer
// ─────────────────────────────────────────────────────────────────────────────

function StickyStatusStrip({ ont, refreshing = false, refreshedAt = null, onManualRefresh }: {
  ont: OntDetailData; refreshing?: boolean; refreshedAt?: number | null; onManualRefresh?: () => void;
}) {
  // Compute "polled X ago" — prefer the live refresh timestamp if recent
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const baseMs = refreshedAt ?? new Date(ont.last_seen_at).getTime();
  const ageSec = Math.floor((Date.now() - baseMs) / 1000);
  const polledLabel = refreshing
    ? 'pulling live…'
    : ageSec < 5 && refreshedAt
      ? 'pulled just now'
      : ageSec < 60
        ? `polled il y a ${ageSec}s`
        : `polled il y a ${Math.floor(ageSec / 60)} min`;
  const statusInfo = {
    up:           { label: 'ONT online · oper UP',     color: 'text-success', bg: 'from-success/10' },
    down:         { label: 'ONT offline · oper DOWN',  color: 'text-danger',  bg: 'from-danger/10' },
    discovering:  { label: 'ONT discovery in progress', color: 'text-info',    bg: 'from-info/10' },
    unknown:      { label: 'ONT status unknown',       color: 'text-text-muted', bg: 'from-bg-2' },
  }[ont.oper_status] ?? { label: ont.oper_status, color: 'text-text-muted', bg: 'from-bg-2' };

  const opt = ont.optical;
  const losActive = (opt?.ont_rx_dbm ?? 0) < -28;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r ${statusInfo.bg} to-transparent border border-border rounded-lg`}>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center ${statusInfo.color} bg-current/10 border border-current/30 ${ont.oper_status === 'up' ? 'animate-firefly' : ''}`}>
          {ont.oper_status === 'up' ? <Wifi size={13} /> : ont.oper_status === 'down' ? <AlertTriangle size={13} /> : <Loader2 size={13} className="animate-spin" />}
        </span>
        <div>
          <div className={`text-sm font-semibold ${statusInfo.color}`}>{statusInfo.label}</div>
          <div className="text-[10.5px] text-text-muted">uptime <Copyable value={formatUptime(ont.metadata.uptime_seconds as number)} /></div>
        </div>
      </div>
      <div className="w-px h-9 bg-border shrink-0" />
      <StripCell label="LOS"          value={losActive ? 'ACTIVE' : 'clear'} valueClass={losActive ? 'text-danger' : 'text-success'} />
      <StripCell label="Optical Rx"   value={opt?.ont_rx_dbm != null ? `${opt.ont_rx_dbm} dBm` : '—'} valueClass={rxColor(opt?.ont_rx_dbm ?? null)} bold />
      <StripCell label="Optical Tx"   value={opt?.ont_tx_dbm != null ? `+${opt.ont_tx_dbm} dBm` : '—'} />
      <StripCell label="BIP"          value={`${(opt?.upstream_bip ?? 0) + (opt?.downstream_bip ?? 0)}`} valueClass={(opt?.upstream_bip ?? 0) + (opt?.downstream_bip ?? 0) > 1000 ? 'text-danger' : 'text-text-muted'} />
      <StripCell label="Distance"     value={opt?.fiber_distance_m != null ? `${opt.fiber_distance_m} m` : '—'} valueClass="text-text-muted" />
      <div className="flex-1" />
      <button
        onClick={onManualRefresh}
        disabled={refreshing}
        title="Cliquer pour pull live"
        className={`text-[10.5px] font-mono text-text-faint shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-surface-hi/40 transition-colors ${refreshing ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
      >
        {refreshing
          ? <Loader2 size={11} className="animate-spin text-info" />
          : <span className={`dot ${ont.oper_status === 'up' ? 'dot-up' : 'dot-down'}`} />}
        <span className={refreshedAt && (Date.now() - refreshedAt) < 5000 ? 'text-success' : ''}>{polledLabel}</span>
      </button>
    </div>
  );
}

function StripCell({ label, value, valueClass, bold = false }: { label: string; value: string; valueClass?: string; bold?: boolean }) {
  return (
    <div className="flex flex-col min-w-[64px]">
      <span className="text-[9.5px] font-medium uppercase tracking-wider text-text-faint">{label}</span>
      <span className={`text-[13px] font-mono ${bold ? 'font-semibold' : 'font-medium'} ${valueClass ?? 'text-text'}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header card
// ─────────────────────────────────────────────────────────────────────────────

function HeaderCard({ ont, onRefresh, refreshing }: { ont: OntDetailData; onRefresh?: () => void; refreshing?: boolean }) {
  return (
    <div className="card card-pad flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {ont.customer ? (
            <Link to={`/app/customers/${ont.customer.id}`} className="text-lg font-semibold tracking-tight hover:text-brand">
              {ont.customer.name}
            </Link>
          ) : (
            <h1 className="text-lg font-semibold tracking-tight">Customer non linké</h1>
          )}
          {ont.customer?.external_id && (
            <Link to={`/app/customers/${ont.customer.id}`} className="badge font-mono hover:text-brand">{ont.customer.external_id}</Link>
          )}
          {ont.link_pending && <span className="badge badge-warn"><AlertTriangle size={9} />link pending</span>}
          <span className="badge badge-info">tenant · telkel</span>
        </div>
        <div className="flex items-center gap-4 mt-1.5 text-xs text-text-muted">
          <span>{ont.customer?.address_line1 ?? <span className="italic text-text-faint">adresse à renseigner</span>}</span>
          <span className="w-px h-3 bg-border" />
          {ont.customer?.phone ? <Copyable value={ont.customer.phone} /> : <span className="italic text-text-faint">téléphone à renseigner</span>}
          <span className="w-px h-3 bg-border" />
          <span>Découvert via OLT <span className="font-mono">{ont.customer?.created_at ? new Date(ont.customer.created_at).toLocaleDateString() : '—'}</span></span>
          {ont.customer?.status === 'discovered' && <>
            <span className="w-px h-3 bg-border" />
            <span className="badge badge-warn text-[10px]">données à compléter</span>
          </>}
        </div>
      </div>

      <div className="flex flex-col gap-1 pl-4 border-l border-border min-w-[260px]">
        <IdRow label="Serial"   value={<Copyable value={ont.vendor_serial} />} />
        <IdRow label="Model"    value={<span className="text-text-muted">{ont.model}</span>} />
        <IdRow label="Firmware" value={<Copyable value={ont.firmware} />} />
        <IdRow label="PON path" value={
          <Link to={`/app/onts?olt_id=${ont.olt.id}`} className="text-brand font-mono hover:underline">
            {ont.olt.name} · {ont.pon.vendor_local_id}/{ont.vendor_local_id}
          </Link>
        } />
      </div>

      <div className="flex flex-col gap-1.5">
        <button className="btn btn-primary h-8" title="Reboot ONT (admin write requis)">
          <RotateCw size={11} />Reboot ONT
        </button>
        <div className="flex gap-1.5">
          <button className="btn" onClick={onRefresh} disabled={refreshing} title="Re-poll OLT + optical metrics + UNI status">
            {refreshing ? <Loader2 size={11} className="animate-spin" /> : <Activity size={11} />}
            Refresh
          </button>
          <Link to={`/app/onts?olt_id=${ont.olt.id}`} className="btn">Voir OLT <ChevronRight size={10} /></Link>
        </div>
      </div>
    </div>
  );
}

function IdRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-[10px] uppercase tracking-wider font-medium text-text-faint">{label}</span>
      <span>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tile row — 6 stats above the fold
// ─────────────────────────────────────────────────────────────────────────────

function TileRow({ ont }: { ont: OntDetailData }) {
  const opt = ont.optical;
  const uni0 = ont.uni_ports[0];
  const fxs0 = ont.fxs_ports[0];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
      <BigTile
        label="Optical Rx"
        value={opt?.ont_rx_dbm != null ? `${opt.ont_rx_dbm}` : '—'}
        unit="dBm"
        valueClass={rxColor(opt?.ont_rx_dbm ?? null)}
        sub={<>seuil warn <span className="font-mono">−25</span> · crit <span className="font-mono">−28</span></>}
        live
      />
      <BigTile
        label="Optical Tx"
        value={opt?.ont_tx_dbm != null ? `+${opt.ont_tx_dbm}` : '—'}
        unit="dBm"
        sub="dans la plage"
      />
      <BigTile
        label="UNI 1"
        value={uni0 ? (uni0.oper_status === 'up' ? 'up' : 'down') : '—'}
        valueClass={uni0?.oper_status === 'up' ? 'text-success' : 'text-text-faint'}
        sub={uni0 ? 'ETH gigabit' : 'not enumerated'}
      />
      <BigTile
        label="FXS 1"
        value={fxs0?.voice_subscription?.registration_state ?? '—'}
        valueClass={fxs0?.voice_subscription?.registration_state === 'Registered' ? 'text-success' : 'text-text-faint'}
        sub={fxs0?.voice_subscription?.phone_number ? <Copyable value={fxs0.voice_subscription.phone_number} /> : 'no voice line'}
      />
      <BigTile
        label="Service"
        value={ont.subscription?.profiles.bandwidth?.name ?? '—'}
        valueClass={ont.subscription ? 'text-text' : 'text-text-faint'}
        sub={ont.subscription
          ? (ont.subscription.profiles.voice ? 'data + voix · actif' : 'data · actif')
          : 'pas de subscription'}
        alert={!ont.subscription}
      />
      <BigTile
        label="Alarmes"
        value={ont.active_alarms_count.toString()}
        valueClass={ont.active_alarms_count > 0 ? 'text-warning' : 'text-text'}
        sub={ont.active_alarms_count > 0 ? 'à investiguer' : 'aucune active'}
      />
    </div>
  );
}

function BigTile({ label, value, unit, valueClass, sub, live, alert }: {
  label: string; value: React.ReactNode; unit?: string; valueClass?: string; sub: React.ReactNode; live?: boolean; alert?: boolean;
}) {
  return (
    <div className="card card-pad relative overflow-hidden !p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-medium uppercase tracking-wider text-text-faint">{label}</span>
        {live && <span className={`dot ${valueClass?.includes('success') ? 'dot-up' : valueClass?.includes('danger') ? 'dot-down' : valueClass?.includes('warning') ? 'dot-warn' : 'dot-up'} animate-pulse-soft`} />}
        {alert && <AlertTriangle size={11} className="text-warning" />}
      </div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className={`font-mono text-xl font-medium tracking-tight ${valueClass ?? 'text-text'}`}>{value}</span>
        {unit && <span className="font-mono text-xs text-text-faint">{unit}</span>}
      </div>
      <div className="text-[10.5px] text-text-faint mt-0.5">{sub}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-elements (UNI + FXS port cards)
// ─────────────────────────────────────────────────────────────────────────────

function SubElements({ ont }: { ont: OntDetailData }) {
  const uniPorts = ont.uni_ports;
  const fxsPorts = ont.fxs_ports;
  const cpes = ont.cpe_devices ?? [];
  // Render at least 1 UNI placeholder if none (since most ONT models have ≥1 UNI ETH)
  const hasNoSubElems = uniPorts.length === 0 && fxsPorts.length === 0;
  return (
    <Card title={<>Sub-elements <span className="font-normal text-text-faint ml-1.5">{uniPorts.length + fxsPorts.length} ports physiques</span></>}>
      {hasNoSubElems ? (
        <div className="text-xs text-text-muted italic py-4 text-center">
          Aucun sub-element enuméré pour cet ONT. UNI ETH / FXS apparaîtront ici après une discovery complète (phase 2 — bridge à étendre).
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {uniPorts.map((u) => {
            // Cross-reference connected MACs (from OLT mac-table) with TR-069 CPEs (devices.mac_address)
            // Tri : v4 d'abord (plus utile), v6-seul après
            const connectedMacs = [...(u.connected_devices ?? [])].sort((a, b) => {
              const av = a.ipv4 ? 0 : a.ipv6 ? 1 : 2;
              const bv = b.ipv4 ? 0 : b.ipv6 ? 1 : 2;
              return av - bv;
            });
            const fields: Array<[string, React.ReactNode, string]> = [
              ['Link state',     u.oper_status,                            u.oper_status === 'up' ? 'text-success' : 'text-danger'],
              ['Speed négocié',  u.config_status && u.config_status !== 'Unknown' ? u.config_status : '—',  u.config_status?.startsWith('1000') ? 'text-success' : u.config_status?.startsWith('100') ? 'text-warning' : 'text-text-muted'],
              ['MAC limit',      u.max_mac != null && u.max_mac > 0 ? String(u.max_mac) : '—', 'text-text-muted'],
              ['Max frame size', u.max_frame_size != null && u.max_frame_size > 0 ? `${u.max_frame_size} B` : '—', 'text-text-muted'],
              ['BW profile',     ont.subscription?.profiles.bandwidth?.name ?? <span className="text-text-faint italic">no sub</span>, 'text-text'],
            ];
            return (
              <div key={u.id} className="bg-bg-2 border border-border rounded p-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-6 h-6 rounded bg-surface-hi flex items-center justify-center text-brand">
                    <Cable size={13} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">UNI {u.vendor_local_id}</div>
                    <div className="text-[10.5px] text-text-faint">
                      Ethernet
                      {u.mac_scan_at && <span> · scanné {new Date(u.mac_scan_at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                    </div>
                  </div>
                  <StatusBadge status={u.oper_status} />
                </div>
                {/* UNI status fields */}
                <div className="flex flex-col gap-1">
                  {fields.map(([k, v, c], i) => (
                    <div key={i} className="flex justify-between items-baseline text-[11.5px]">
                      <span className="text-text-muted">{k}</span>
                      <span className={`font-mono ${c}`}>{v}</span>
                    </div>
                  ))}
                </div>
                {/* Connected devices (from OLT mac-table — visible même si pas en TR-069) */}
                {connectedMacs.length > 0 ? (
                  <div className="mt-3 pt-2 border-t border-border">
                    <div className="text-[10px] text-text-faint uppercase tracking-wider font-medium mb-1.5">
                      Devices connectés ({connectedMacs.length})
                    </div>
                    <div className="flex flex-col gap-1">
                      {connectedMacs.map((d, i) => {
                        const tr069 = cpes.find((c) => c.mac_address?.toLowerCase() === d.mac);
                        const isStatic = d.type?.toLowerCase() === 'static';
                        return (
                          <div key={i} className="flex flex-col gap-0.5 py-1 border-b border-border/50 last:border-0">
                            <div className="flex items-center gap-2 text-[11px]">
                              <span className="font-mono text-text-muted">{d.mac}</span>
                              {d.vendor && (
                                <span className="badge badge-info text-[9px]" title={`OUI vendor: ${d.vendor}`}>{d.vendor}</span>
                              )}
                              {tr069 ? (
                                <Link to={`/app/devices/${tr069.id}`} className="badge badge-brand text-[9px]" title="Géré en TR-069">TR-069</Link>
                              ) : (
                                <span className="badge text-[9px] text-text-faint">unmanaged</span>
                              )}
                              <span className={`badge text-[9px] ${isStatic ? 'text-warning' : 'text-text-faint'}`} title={isStatic ? 'IP statique configurée' : 'DHCP'}>
                                {isStatic ? 'static' : 'dhcp'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-[10.5px] pl-4">
                              {d.ipv4 && <span><span className="text-text-faint">v4</span> <span className="font-mono text-brand">{d.ipv4}</span></span>}
                              {d.ipv6 && <span><span className="text-text-faint">v6</span> <span className="font-mono text-text-muted" title={d.ipv6}>{d.ipv6.length > 30 ? d.ipv6.slice(0, 30) + '…' : d.ipv6}</span></span>}
                              {!d.ipv4 && !d.ipv6 && <span className="italic text-text-faint">pas d'IP visible</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 pt-2 border-t border-border text-[10.5px] text-text-faint italic">
                    Aucun device détecté sur cette UNI. Sera scanné au prochain run de cpe:link-by-mac (toutes les 30 min).
                  </div>
                )}
              </div>
            );
          })}
          {fxsPorts.map((f) => {
            // IP utilisée par le téléphone = IP du CPE qui fait le SIP (généralement
            // le routeur SmartRG/Calix de cet ONT). On prend le premier CPE avec wan_ip.
            const voiceCpe = cpes.find((c) => c.wan_ip) ?? cpes[0];
            const voiceIp = voiceCpe?.public_wan_ip ?? voiceCpe?.wan_ip ?? null;
            return (
              <PortCard
                key={f.id}
                icon={<Phone size={13} />}
                name={`FXS ${f.vendor_local_id}`}
                kind="Telephone"
                status={f.voice_subscription?.registration_state === 'Registered' ? 'up' : 'down'}
                fields={[
                  ['Numéro',       f.voice_subscription?.phone_number ?? '—', 'text-text'],
                  ['Registration', f.voice_subscription?.registration_state ?? '—', f.voice_subscription?.registration_state === 'Registered' ? 'text-success' : 'text-warning'],
                  ['SIP user',     f.voice_subscription?.sip_auth_username ?? '—', 'text-text-muted'],
                  ['IP voice',     voiceIp ?? '—', voiceIp ? 'text-brand' : 'text-text-faint'],
                  ['Voice profile',f.voice_subscription?.voice_profile?.name ?? '—', 'text-text-muted'],
                ]}
                action={<button className="btn btn-sm"><Edit3 size={9} />Edit voice</button>}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}

function PortCard({ icon, name, kind, status, fields, action }: {
  icon: React.ReactNode; name: string; kind: string; status: string;
  fields: Array<[string, React.ReactNode, string]>; action?: React.ReactNode;
}) {
  return (
    <div className="bg-bg-2 border border-border rounded p-3">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-6 h-6 rounded bg-surface-hi flex items-center justify-center text-brand">{icon}</div>
        <div className="flex-1">
          <div className="text-sm font-semibold">{name}</div>
          <div className="text-[10.5px] text-text-faint">{kind}</div>
        </div>
        <StatusBadge status={status} />
      </div>
      <div className="flex flex-col gap-1">
        {fields.map(([k, v, c], i) => (
          <div key={i} className="flex justify-between items-baseline text-[11.5px]">
            <span className="text-text-muted">{k}</span>
            <span className={`font-mono ${c}`}>{v}</span>
          </div>
        ))}
      </div>
      {action && <div className="mt-2.5 pt-2 border-t border-border flex justify-end">{action}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CPE devices (TR-069) — WAN IP v4/v6 derrière l'ONT
// ─────────────────────────────────────────────────────────────────────────────

function CpeDevicesCard({ ont }: { ont: OntDetailData }) {
  const cpes = ont.cpe_devices ?? [];
  if (cpes.length === 0) {
    return (
      <Card title={<>CPE / WAN IPs <span className="text-text-faint font-normal ml-1.5">aucun CPE TR-069 lié</span></>}>
        <div className="py-4 text-xs text-text-muted italic text-center">
          Pas de routeur TR-069 enregistré pour ce client.
          Les IPs WAN s'afficheront ici quand le CPE Calix/SmartRG aura fait son premier Inform.
        </div>
      </Card>
    );
  }
  return (
    <Card title={<>CPE / WAN IPs <span className="text-text-faint font-normal ml-1.5">{cpes.length} routeur{cpes.length > 1 ? 's' : ''}</span></>}>
      <div className="flex flex-col gap-2">
        {cpes.map((d) => (
          <div key={d.id} className="bg-bg-2 border border-border rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className={`dot ${d.is_online ? 'dot-up' : 'dot-down'}`} />
              <Link to={`/app/devices/${d.id}`} className="text-sm font-semibold hover:text-brand">
                {d.manufacturer ?? '?'} {d.model_name ?? ''}
              </Link>
              <span className="badge font-mono text-[10px]">{d.serial_number}</span>
              {d.is_nat && <span className="badge badge-warn text-[10px]">NAT</span>}
              <div className="ml-auto text-[10px] text-text-faint font-mono">
                {d.last_inform_at ? `inform ${new Date(d.last_inform_at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : 'pas d\'inform'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px]">
              <KvRow k="MAC" v={d.mac_address} mono />
              <KvRow k="Firmware" v={d.firmware} mono />
              <KvRow k="WAN IPv4 (CPE)" v={d.wan_ip} mono highlight={!!d.wan_ip} />
              <KvRow k="Public IPv4 (NAT)" v={d.public_wan_ip} mono highlight={!!d.public_wan_ip && d.is_nat} />
              <KvRow k="WAN IPv6" v={d.wan_ipv6} mono />
              <KvRow k="IPv6 PD" v={d.wan_ipv6_pd} mono />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function KvRow({ k, v, mono, highlight }: { k: string; v: string | null | undefined; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-text-faint text-[10.5px] uppercase tracking-wider font-medium">{k}</span>
      {v
        ? <span className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-brand font-medium' : 'text-text'}`}>{v}</span>
        : <span className="italic text-text-faint">—</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subscription card with inline bandwidth profile change
// ─────────────────────────────────────────────────────────────────────────────

function SubscriptionCard({ ont }: { ont: OntDetailData }) {
  const [editing, setEditing] = useState(false);

  if (!ont.subscription) {
    return (
      <Card title="Subscription">
        <div className="py-5 flex flex-col items-center gap-2 text-text-muted">
          <AlertTriangle size={18} className="text-warning" />
          <div className="font-semibold text-text">Aucune subscription assignée</div>
          <div className="text-xs text-center max-w-md">
            L'ONT est en service mais aucun bundle n'est appliqué. Le trafic est bloqué côté OLT.<br />
            Les subscriptions arriveront lors de l'import CRM ou tu peux en assigner une manuellement.
          </div>
          <button className="btn btn-primary mt-1"><Sparkles size={11} />Assigner un bundle</button>
        </div>
      </Card>
    );
  }

  const sub = ont.subscription;
  // Build effective bandwidth label : profile or custom override
  const bw = sub.profiles.bandwidth;
  const bwLabel = bw
    ? (bw.is_parameterized && sub.custom_up_kbps
        ? `Custom ${(sub.custom_up_kbps/1000)}M ↑ / ${(sub.custom_down_kbps??0)/1000}M ↓`
        : bw.name)
    : '—';

  // 4 dimensions de profils (some may be null for data-only customers)
  const dims = [
    { kind: 'bandwidth',       label: 'Bandwidth',    p: sub.profiles.bandwidth,       custom: bwLabel },
    { kind: 'wan_access',      label: 'WAN Access',   p: sub.profiles.wan_access },
    { kind: 'voice',           label: 'Voice',        p: sub.profiles.voice },
    { kind: 'voice_transport', label: 'Voice transport', p: sub.profiles.voice_transport },
  ];

  return (
    <Card
      title={<>Subscription <span className="badge badge-brand ml-1.5">{sub.status}</span></>}
      actions={<button className="btn btn-sm btn-ghost" onClick={() => setEditing((v) => !v)}>
        <Edit3 size={9} />{editing ? 'Annuler' : 'Modifier'}
      </button>}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded bg-brand-dim flex items-center justify-center text-brand">
          <Sparkles size={16} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">{bwLabel}</div>
          <div className="text-[11.5px] text-text-faint">
            activé <span className="font-mono">{sub.activated_at ? new Date(sub.activated_at).toLocaleDateString() : '—'}</span>
            {sub.profiles.voice && <span> · avec voix</span>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {dims.map((d) => (
          <div key={d.kind} className={`flex items-center gap-2 px-2 py-1.5 rounded border ${d.p ? 'bg-bg-2 border-border' : 'bg-bg-2/40 border-dashed border-border opacity-60'}`}>
            <span className={`w-1 h-5 rounded-full shrink-0 ${d.p ? 'bg-brand' : 'bg-text-faint'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-text-faint uppercase tracking-wider font-medium">{d.label}</div>
              <div className={`text-[11.5px] truncate ${d.p ? 'font-medium' : 'italic text-text-faint'}`}>
                {d.custom ?? d.p?.name ?? 'non assigné'}
              </div>
              {d.p?.vlan && <div className="text-[10px] text-text-faint font-mono">VLAN {d.p.vlan}{d.p.ipv6_mode && d.p.ipv6_mode !== 'same_as_v4' ? ` · v6 ${d.p.ipv6_mode}` : ''}</div>}
            </div>
          </div>
        ))}
      </div>
      {editing && bw && <BandwidthChanger sub={sub} onClose={() => setEditing(false)} />}
    </Card>
  );
}

function BandwidthChanger({ sub, onClose }: { sub: NonNullable<OntDetailData['subscription']>; onClose: () => void }) {
  const { data: profiles } = useQuery({
    queryKey: ['bandwidth-profiles'],
    queryFn: () => serviceProfiles.list('bandwidth'),
  });

  const current = sub.profiles.bandwidth;
  const [selectedId, setSelectedId] = useState<number>(current?.id ?? 0);
  const [customUp, setCustomUp] = useState(sub.custom_up_kbps ?? 0);
  const [customDown, setCustomDown] = useState(sub.custom_down_kbps ?? 0);
  const selected = profiles?.find((p) => p.id === selectedId);

  return (
    <div className="mt-3 pt-3 border-t border-border flex flex-col gap-2">
      <div className="text-xs font-medium">Changer le profil de vitesse</div>
      <div className="flex items-center gap-2">
        <select className="input flex-1" value={selectedId} onChange={(e) => setSelectedId(Number(e.target.value))}>
          {profiles?.map((p: ServiceProfileItem) => (
            <option key={p.id} value={p.id}>
              {p.name} {!p.is_parameterized && p.up_kbps ? `(${formatBitrate(p.up_kbps)})` : '(custom)'}
            </option>
          ))}
        </select>
        {selected?.is_parameterized && (
          <>
            <input type="number" className="input w-20" placeholder="up kbps" value={customUp || ''} onChange={(e) => setCustomUp(Number(e.target.value))} />
            <input type="number" className="input w-20" placeholder="down kbps" value={customDown || ''} onChange={(e) => setCustomDown(Number(e.target.value))} />
          </>
        )}
      </div>
      <div className="flex justify-end gap-1.5">
        <button className="btn btn-sm" onClick={onClose}>Annuler</button>
        <button className="btn btn-sm btn-primary" disabled title="Admin write creds requis sur l'OLT">Apply (501)</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Optical history chart
// ─────────────────────────────────────────────────────────────────────────────

function OpticalHistoryCard({ data }: { data: Array<{ at: string; ont_rx_dbm: number | null; ont_tx_dbm: number | null; olt_rx_dbm: number | null }> }) {
  const chartData = data.map((d) => ({
    t: new Date(d.at).getTime(),
    ontRx: d.ont_rx_dbm,
    ontTx: d.ont_tx_dbm,
    oltRx: d.olt_rx_dbm,
  }));
  return (
    <Card title="Optical history · 7 jours">
      {chartData.length < 2 ? (
        <div className="text-xs text-text-muted text-center py-6 italic">
          Pas encore assez d'échantillons. La courbe apparaîtra après plusieurs discoveries (15min interval).
        </div>
      ) : (
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid stroke="var(--tw-color-border, #2e3038)" strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="t" type="number" domain={['dataMin', 'dataMax']}
                tickFormatter={(t) => new Date(t).toLocaleDateString('fr', { day: '2-digit', month: '2-digit' })}
                tick={{ fontSize: 10, fill: '#6c7079' }}
              />
              <YAxis domain={[-32, 5]} tick={{ fontSize: 10, fill: '#6c7079' }} unit=" dBm" />
              <ReferenceLine y={-25} stroke="#e9a851" strokeDasharray="3 3" label={{ value: 'warn', fill: '#e9a851', fontSize: 10, position: 'right' }} />
              <ReferenceLine y={-28} stroke="#e36363" strokeDasharray="3 3" label={{ value: 'crit', fill: '#e36363', fontSize: 10, position: 'right' }} />
              <Area type="monotone" dataKey="ontRx" stroke="#56b4e9" fill="#56b4e9" fillOpacity={0.2} name="ONT Rx" />
              <Area type="monotone" dataKey="ontTx" stroke="#009e73" fill="#009e73" fillOpacity={0.15} name="ONT Tx" />
              <Area type="monotone" dataKey="oltRx" stroke="#f0e442" fill="#f0e442" fillOpacity={0.1} name="OLT Rx" />
              <Tooltip
                labelFormatter={(t) => new Date(t).toLocaleString('fr')}
                contentStyle={{ background: '#1c1d23', border: '1px solid #2e3038', borderRadius: 4, fontSize: 11 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Events / alarms timeline
// ─────────────────────────────────────────────────────────────────────────────

function EventsTimeline({ events, alarms }: { events: Array<{ id: number; kind: string; at: string; details: Record<string, unknown> | null }>, alarms: Array<{ id: number; code: string; name: string; severity: string; raised_at: string; cleared_at: string | null }> }) {
  // Merge into a single timeline
  const items = [
    ...alarms.map((a) => ({ id: `a${a.id}`, code: a.code, label: a.name, t: a.raised_at, cleared: !!a.cleared_at, severity: a.severity })),
    ...events.map((e) => ({ id: `e${e.id}`, code: e.kind.toUpperCase(), label: (e.details?.message as string) ?? e.kind, t: e.at, cleared: true, severity: 'info' })),
  ].sort((a, b) => new Date(b.t).getTime() - new Date(a.t).getTime());

  return (
    <Card title={<>Alarms &amp; events <span className="text-text-faint font-normal ml-1.5">14 derniers jours</span></>}>
      {items.length === 0 ? (
        <div className="text-xs text-text-muted italic py-4 text-center">Aucun événement dans les 14 derniers jours.</div>
      ) : (
        <div className="relative pl-5">
          <div className="absolute left-1.5 top-1 bottom-1 w-px bg-border" />
          {items.slice(0, 20).map((e) => {
            const color = e.severity === 'critical' ? 'text-danger' : e.severity === 'warning' ? 'text-warning' : e.severity === 'info' ? 'text-info' : 'text-success';
            return (
              <div key={e.id} className="relative pb-2.5">
                <span className={`absolute -left-[17px] top-1 w-2.5 h-2.5 rounded-full border-2 ${e.cleared ? 'bg-transparent' : 'bg-current'} ${color} border-current`} />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-mono text-[11px] font-semibold px-1.5 py-0.5 rounded border ${e.cleared ? 'border-border text-text-muted' : 'border-current ' + color}`}>{e.code}</span>
                  <span className="text-xs font-medium">{e.label}</span>
                  {e.cleared ? <span className="badge badge-up text-[10px]">cleared</span> : <span className="badge badge-down text-[10px]">active</span>}
                  <div className="flex-1" />
                  <span className="font-mono text-[10.5px] text-text-faint">{new Date(e.t).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Advanced section (seat, OMCI, debug)
// ─────────────────────────────────────────────────────────────────────────────

function AdvancedSection({ ont }: { ont: OntDetailData }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-3.5 py-2.5 flex items-center gap-2 hover:bg-surface-hi/30">
        <ChevronRight size={11} className={`text-text-muted transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="text-sm font-semibold">Advanced</span>
        <span className="text-[11px] text-text-faint">seat · raw config · debug</span>
      </button>
      {open && (
        <div className="px-3.5 pb-3 grid grid-cols-2 gap-2 text-xs">
          <KvAdv k="Seat (vendor_local_id)" v={ont.vendor_local_id} />
          <KvAdv k="ONT serial" v={ont.vendor_serial ?? '—'} />
          <KvAdv k="External ID" v={ont.external_id ?? '—'} />
          <KvAdv k="Admin status" v={ont.admin_status} />
          <KvAdv k="Last seen" v={new Date(ont.last_seen_at).toLocaleString('fr')} />
          <KvAdv k="OLT driver" v={ont.olt.driver} />
        </div>
      )}
    </div>
  );
}

function KvAdv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between py-1 border-b border-dashed border-border last:border-0">
      <span className="text-text-muted">{k}</span>
      <span className="font-mono text-text">{v}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Right rail — Actions, Topology, Diagnostics
// ─────────────────────────────────────────────────────────────────────────────

function ActionsRail({ ont, onAction }: { ont: OntDetailData; onAction: (msg: string) => void }) {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [intPaused, setIntPaused] = useState(false);
  const [voicePaused, setVoicePaused] = useState(false);

  // Note: actual pause/unsubscribe API calls would be wired here.
  // For MVP, all return 501 — UI shows a toast with the expected behavior.

  return (
    <>
      <Card title="Actions">
        {/* Pause toggles */}
        <div className="flex flex-col gap-1.5">
          <ServiceToggle
            label="Service Internet"
            kind="internet"
            paused={intPaused}
            onToggle={() => {
              setIntPaused((v) => !v);
              onAction(intPaused ? 'Internet repris (501 — admin write requis)' : 'Internet en pause (501 — admin write requis)');
            }}
          />
          <ServiceToggle
            label="Service Téléphone"
            kind="voice"
            paused={voicePaused}
            onToggle={() => {
              setVoicePaused((v) => !v);
              onAction(voicePaused ? 'Voix reprise (501 — admin write requis)' : 'Voix en pause (501 — admin write requis)');
            }}
          />
        </div>

        <div className="my-3 border-t border-border" />

        <div className="flex flex-col gap-1">
          <ActionRow icon={<RotateCw size={12} />} label="Reboot ONT" sub="~45s indisponible" onClick={() => onAction('Reboot ONT (501 — admin write requis)')} />
          <ActionRow icon={<Activity size={12} />} label="Refresh discovery" sub="re-poll optical + state" onClick={() => onAction('Refresh discovery queued')} />
          <ActionRow icon={<Edit3 size={12} />} label="Edit voice credentials" sub="phone / SIP user / password" onClick={() => onAction('Edit voice (501 — admin write requis)')} />
        </div>

        <div className="my-3 border-t border-border" />

        <button
          className="btn w-full text-danger border-danger/30 bg-danger/5 hover:bg-danger/15"
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2 size={11} />Désabonner client
        </button>
      </Card>

      <Modal open={confirmOpen} onClose={() => { setConfirmOpen(false); setConfirmText(''); }} title="Désabonner ce client ?">
        <div className="space-y-3 text-sm">
          <div className="text-text-muted">
            Cette action va envoyer la séquence <span className="font-mono text-danger">no ...</span> à l'OLT pour supprimer toute la config de ce client :
          </div>
          <ul className="text-xs font-mono space-y-0.5 text-text-muted bg-bg-2 rounded p-2">
            <li>no evc-map {ont.customer?.external_id ?? '???'}_DATA</li>
            <li>no evc-map {ont.customer?.external_id ?? '???'}_VOICE</li>
            <li>no shaper {ont.customer?.external_id ?? '???'}-up / -dow</li>
            <li>no voice user sip {ont.vendor_local_id}/0/1@{ont.pon.vendor_local_id}</li>
            <li>no interface ip-host {ont.customer?.external_id ?? '???'}</li>
            <li>no remote-device ont {ont.vendor_local_id}@{ont.pon.vendor_local_id}.gpon</li>
          </ul>
          <div className="text-xs text-text-muted">
            Le seat sera libéré pour un autre client. Le customer record (ID {ont.customer?.id ?? '—'}) sera conservé.
          </div>
          <div className="text-xs">
            Confirme en tapant l'external_id <span className="font-mono text-brand">{ont.customer?.external_id ?? '???'}</span> :
            <input
              type="text"
              className="input w-full mt-1 font-mono"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="external_id"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn" onClick={() => { setConfirmOpen(false); setConfirmText(''); }}>Annuler</button>
            <button
              className="btn btn-primary !bg-danger !text-white !border-danger hover:!bg-danger/80"
              disabled={confirmText !== ont.customer?.external_id}
              onClick={() => {
                onAction('Désabonnement (501 — admin write requis ; configuration de prod préservée)');
                setConfirmOpen(false); setConfirmText('');
              }}
            >
              <Trash2 size={11} />Confirmer désabonnement
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ServiceToggle({ label, kind, paused, onToggle }: { label: string; kind: 'internet' | 'voice'; paused: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center justify-between px-2 py-2 rounded hover:bg-surface-hi/40">
      <div className="flex items-center gap-2">
        <span className={`dot ${paused ? 'dot-warn' : 'dot-up'}`} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <button
        onClick={onToggle}
        className={`relative w-9 h-5 rounded-full transition-colors ${paused ? 'bg-warning/30' : 'bg-success/30'}`}
        title={paused ? `Reprendre ${kind}` : `Pause ${kind}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${paused ? 'left-0.5 bg-warning' : 'left-[18px] bg-success'}`} />
      </button>
    </div>
  );
}

function ActionRow({ icon, label, sub, onClick }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-surface-hi/40 text-left transition-colors">
      <div className="w-6 h-6 rounded bg-surface-hi flex items-center justify-center text-text-muted">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium">{label}</div>
        <div className="text-[10.5px] text-text-faint">{sub}</div>
      </div>
      <ChevronRight size={10} className="text-text-faint" />
    </button>
  );
}

function TopologyRail({ ont }: { ont: OntDetailData }) {
  const cpes = ont.cpe_devices ?? [];
  return (
    <Card title="Topologie">
      <div className="flex flex-col gap-1 text-xs">
        <TopologyRow icon={<Activity size={11} />} label="OLT" name={ont.olt.name} sub={`${ont.olt.vendor} · ${ont.olt.management_address}`} />
        <div className="w-px h-2 bg-border ml-3" />
        <TopologyRow icon={<Cable size={11} />} label={`PON ${ont.pon.vendor_local_id}`} name={ont.pon.label ?? ''} sub={`${(ont.pon.metadata?.configured_onts as number) ?? 0} ONTs configurés`} />
        <div className="w-px h-2 bg-border ml-3" />
        <TopologyRow icon={<Wifi size={11} />} label={`ONT seat ${ont.vendor_local_id}`} name={`${ont.vendor_serial ?? '—'}`} sub="ce device" highlight />
        {(ont.uni_ports.length > 0 || ont.fxs_ports.length > 0) && (
          <>
            <div className="w-px h-2 bg-border ml-3" />
            <div className="grid grid-cols-2 gap-1.5 mt-0.5">
              {ont.uni_ports.map((u) => {
                const devs = u.connected_devices ?? [];
                return (
                  <div key={u.id} className="flex flex-col gap-1">
                    <TopologyRow icon={<Cable size={9} />} label={`UNI ${u.vendor_local_id}`} name={u.oper_status} mini />
                    {devs.length > 0 ? (
                      <div className="ml-2.5 pl-2 border-l border-border flex flex-col gap-1">
                        {devs.slice(0, 2).map((d, i) => {
                          const tr069 = cpes.find((c) => c.mac_address?.toLowerCase() === d.mac);
                          return (
                            <div key={i} className="flex flex-col gap-[1px] text-[9.5px]">
                              <div className="flex items-center gap-1">
                                {tr069 ? <Cpu size={8} className="text-brand shrink-0" /> : <Cable size={8} className="text-text-faint shrink-0" />}
                                <span className="font-mono text-text-muted">{d.mac}</span>
                              </div>
                              <div className="pl-2.5 font-mono">
                                {d.ipv4 && <span className="text-brand">{d.ipv4}</span>}
                                {d.ipv4 && d.ipv6 && <span className="text-text-faint mx-1">·</span>}
                                {d.ipv6 && <span className="text-text-muted" title={d.ipv6}>{d.ipv6.length > 22 ? d.ipv6.slice(0, 22) + '…' : d.ipv6}</span>}
                                {!d.ipv4 && !d.ipv6 && <span className="text-text-faint italic">—</span>}
                              </div>
                            </div>
                          );
                        })}
                        {devs.length > 2 && <span className="text-[9px] text-text-faint">+{devs.length - 2}</span>}
                      </div>
                    ) : (
                      <div className="ml-2.5 pl-2 border-l border-border text-[9.5px] text-text-faint italic">aucun device</div>
                    )}
                  </div>
                );
              })}
              {ont.fxs_ports.map((f) => {
                const vs = f.voice_subscription;
                const reg = vs?.registration_state ?? '—';
                const regOk = reg.toLowerCase() === 'registered' || reg.toLowerCase() === 'up';
                const voiceCpe = cpes.find((c) => c.wan_ip) ?? cpes[0];
                const voiceIp = voiceCpe?.public_wan_ip ?? voiceCpe?.wan_ip ?? null;
                return (
                  <div key={f.id} className="flex flex-col gap-1">
                    <TopologyRow icon={<Phone size={9} />} label={`FXS ${f.vendor_local_id}`} name={reg} mini />
                    {vs ? (
                      <div className="ml-2.5 pl-2 border-l border-border flex flex-col gap-[1px] text-[9.5px]">
                        <div className="flex items-center gap-1">
                          <span className={regOk ? 'text-success' : 'text-text-faint'}>●</span>
                          <span className="font-mono text-text-muted truncate" title={vs.sip_auth_username}>{vs.sip_auth_username}</span>
                        </div>
                        {vs.phone_number && (
                          <div className="pl-2.5 font-mono text-brand truncate" title={vs.phone_number}>{vs.phone_number}</div>
                        )}
                        {voiceIp && (
                          <div className="pl-2.5 font-mono text-text-muted" title={`IP du CPE qui fait le SIP : ${voiceIp}`}>{voiceIp}</div>
                        )}
                      </div>
                    ) : (
                      <div className="ml-2.5 pl-2 border-l border-border text-[9.5px] text-text-faint italic">pas de sub voix</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function TopologyRow({ icon, label, name, sub, highlight, mini }: { icon: React.ReactNode; label: string; name: string; sub?: string; highlight?: boolean; mini?: boolean }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${highlight ? 'bg-brand-dim border-brand/30' : 'bg-bg-2 border-border'}`}>
      <div className={`shrink-0 w-5 h-5 rounded bg-surface-hi flex items-center justify-center ${highlight ? 'text-brand' : 'text-text-muted'}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-text-faint font-medium">{label}</div>
        <div className={`font-mono ${mini ? 'text-[10.5px]' : 'text-xs'} truncate ${highlight ? 'text-brand' : 'text-text'}`}>{name}</div>
        {sub && !mini && <div className="text-[10px] text-text-faint">{sub}</div>}
      </div>
    </div>
  );
}

function DiagnosticsRail({ ont }: { ont: OntDetailData }) {
  const opt = ont.optical;
  const bipTotal = (opt?.upstream_bip ?? 0) + (opt?.downstream_bip ?? 0);
  const checks = [
    { ok: opt?.ont_rx_dbm == null || opt.ont_rx_dbm > -25, label: 'Optical Rx dans la plage', detail: opt?.ont_rx_dbm != null ? `${opt.ont_rx_dbm} dBm` : '—' },
    { ok: (opt?.ont_rx_dbm ?? 0) > -28, label: 'Pas de LOS actif', detail: (opt?.ont_rx_dbm ?? 0) < -28 ? 'LOS actif' : 'clear' },
    { ok: bipTotal < 1000, label: 'BIP < 1000 / 15min', detail: `${bipTotal} erreurs` },
    { ok: ont.oper_status === 'up', label: 'ONT opérationnel', detail: ont.oper_status },
    { ok: !!ont.customer?.id, label: 'Customer linké', detail: ont.customer?.id ? 'OK' : 'pending' },
    { ok: !!ont.subscription, label: 'Subscription assignée', detail: ont.subscription?.profiles.bandwidth?.name ?? 'aucune' },
  ];
  return (
    <Card title="Diagnostic rapide">
      <div className="flex flex-col gap-1">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-2 px-1.5 py-1 rounded">
            <span className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${c.ok ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'}`}>
              {c.ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
            </span>
            <span className={`flex-1 text-[11.5px] ${c.ok ? 'text-text' : 'text-text-muted'}`}>{c.label}</span>
            <span className="font-mono text-[11px] text-text-faint">{c.detail}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card primitive
// ─────────────────────────────────────────────────────────────────────────────

function Card({ title, actions, children }: { title: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border">
        <div className="text-sm font-semibold">{title}</div>
        {actions && <div className="flex items-center gap-1.5">{actions}</div>}
      </div>
      <div className="p-3.5">{children}</div>
    </div>
  );
}
